// Placeholder data for the Unified Creation Modal
// Images are loaded from Azure blob storage

import { QUALITY_TIERS } from '@repo/artwork-generation/image-quality-mapping';
import {
  DEFAULT_PRINT_RATIO_ID,
  PRINT_RATIO_CATALOG,
  normalizeAspectRatioId,
  resolvePrintSizeForAspectRatio,
} from '@repo/artwork-generation/print-ratio-catalog';
import { POPFRAME_DELIVERY_ORIGIN } from '@repo/utils';
import { OptionItem, QualityOption, AspectRatioOption } from './types';
import { CATEGORY_IMAGE_BASE_URLS } from '@/constants/category-image-paths';

// Image URL builders
const STYLES_URL = CATEGORY_IMAGE_BASE_URLS.styles;
const MOOD_IMAGES_URL = `${POPFRAME_DELIVERY_ORIGIN}/images/moods`;
const ROOMS_IMAGES_URL = CATEGORY_IMAGE_BASE_URLS.rooms;
const LIGHTING_IMAGES_URL = `${POPFRAME_DELIVERY_ORIGIN}/images/lighting`;
const COLOR_SCHEMES_URL = `${POPFRAME_DELIVERY_ORIGIN}/images/color_schemes`;
const TECHNIQUES_URL = `${POPFRAME_DELIVERY_ORIGIN}/images/techniques`;
const TIME_OF_DAY_URL = `${POPFRAME_DELIVERY_ORIGIN}/images/time_of_day`;
const SEASONS_URL = `${POPFRAME_DELIVERY_ORIGIN}/images/seasons`;
const DECOR_STYLE_URL = `${POPFRAME_DELIVERY_ORIGIN}/images/decor_styles`;
const AUTO_THUMBNAILS_URL = `${POPFRAME_DELIVERY_ORIGIN}/images/auto_thumbnails`;
const STYLE_BOARD_THUMBNAILS_URL = `${POPFRAME_DELIVERY_ORIGIN}/images/style_boards/thumbnails`;

type TranslateFn = (key: string, values?: Record<string, unknown>) => string;

const translateWithFallback = (
  t: unknown,
  key: string,
  fallback: string,
  values?: Record<string, unknown>,
) => {
  if (typeof t !== 'function') {
    return fallback;
  }

  try {
    return (<TranslateFn>t)(key, values);
  } catch {
    return fallback;
  }
};

const OPTION_NAME_TRANSLATION_KEYS: Record<string, Record<string, string>> = {
  style: {
    default: 'create.shared.optionNames.auto',
    realism: 'create.shared.optionNames.style.realism',
    photorealism: 'create.shared.optionNames.style.photorealism',
    abstract: 'create.shared.optionNames.style.abstract',
    minimalism: 'create.shared.optionNames.style.minimalism',
    pop_art: 'create.shared.optionNames.style.pop_art',
    impressionism: 'create.shared.optionNames.style.impressionism',
    expressionism: 'create.shared.optionNames.style.expressionism',
    surrealism: 'create.shared.optionNames.style.surrealism',
    cubism: 'create.shared.optionNames.style.cubism',
    art_nouveau: 'create.shared.optionNames.style.art_nouveau',
    fauvism: 'create.shared.optionNames.style.fauvism',
    op_art: 'create.shared.optionNames.style.op_art',
    futurism: 'create.shared.optionNames.style.futurism',
    geometric_modern: 'create.shared.optionNames.style.geometric_modern',
    line_art: 'create.shared.optionNames.style.line_art',
  },
  colorScheme: {
    default: 'create.shared.optionNames.auto',
    cool: 'create.shared.optionNames.colorScheme.cool',
    warm: 'create.shared.optionNames.colorScheme.warm',
    pastel: 'create.shared.optionNames.colorScheme.pastel',
    vibrant: 'create.shared.optionNames.colorScheme.vibrant',
    monochrome: 'create.shared.optionNames.colorScheme.monochrome',
  },
  mood: {
    default: 'create.shared.optionNames.auto',
    serene: 'create.shared.optionNames.mood.serene',
    cheerful: 'create.shared.optionNames.mood.cheerful',
    energetic: 'create.shared.optionNames.mood.energetic',
    mysterious: 'create.shared.optionNames.mood.mysterious',
    melancholic: 'create.shared.optionNames.mood.melancholic',
    intense: 'create.shared.optionNames.mood.intense',
  },
  room: {
    default: 'create.shared.optionNames.auto',
    living_room: 'create.shared.optionNames.room.living_room',
    bedroom: 'create.shared.optionNames.room.bedroom',
    dining_room: 'create.shared.optionNames.room.dining_room',
    kitchen: 'create.shared.optionNames.room.kitchen',
    office: 'create.shared.optionNames.room.office',
    bathroom: 'create.shared.optionNames.room.bathroom',
    hallway: 'create.shared.optionNames.room.hallway',
    kids_room: 'create.shared.optionNames.room.kids_room',
    home_gym: 'create.shared.optionNames.room.home_gym',
    outside_space: 'create.shared.optionNames.room.outside_space',
  },
  timeOfDay: {
    default: 'create.shared.optionNames.auto',
    dawn: 'create.shared.optionNames.timeOfDay.dawn',
    sunrise: 'create.shared.optionNames.timeOfDay.sunrise',
    daylight: 'create.shared.optionNames.timeOfDay.daylight',
    afternoon: 'create.shared.optionNames.timeOfDay.afternoon',
    sunset: 'create.shared.optionNames.timeOfDay.sunset',
    twilight: 'create.shared.optionNames.timeOfDay.twilight',
    night: 'create.shared.optionNames.timeOfDay.night',
  },
  lightingStyle: {
    default: 'create.shared.optionNames.auto',
    soft_light: 'create.shared.optionNames.lightingStyle.soft_light',
    bright_clear: 'create.shared.optionNames.lightingStyle.bright_clear',
    warm_glow: 'create.shared.optionNames.lightingStyle.warm_glow',
    cool_light: 'create.shared.optionNames.lightingStyle.cool_light',
    dramatic_shadows: 'create.shared.optionNames.lightingStyle.dramatic_shadows',
    backlit: 'create.shared.optionNames.lightingStyle.backlit',
    studio_light: 'create.shared.optionNames.lightingStyle.studio_light',
  },
  season: {
    default: 'create.shared.optionNames.auto',
    spring: 'create.shared.optionNames.season.spring',
    summer: 'create.shared.optionNames.season.summer',
    autumn: 'create.shared.optionNames.season.autumn',
    winter: 'create.shared.optionNames.season.winter',
  },
  artMedium: {
    default: 'create.shared.optionNames.auto',
    oil_painting: 'create.shared.optionNames.artMedium.oil_painting',
    watercolor: 'create.shared.optionNames.artMedium.watercolor',
    acrylic: 'create.shared.optionNames.artMedium.acrylic',
    ink_drawing: 'create.shared.optionNames.artMedium.ink_drawing',
    pencil_sketch: 'create.shared.optionNames.artMedium.pencil_sketch',
    charcoal_drawing: 'create.shared.optionNames.artMedium.charcoal_drawing',
    digital_art: 'create.shared.optionNames.artMedium.digital_art',
    '3d_render': 'create.shared.optionNames.artMedium.three_d_render',
    mixed_media: 'create.shared.optionNames.artMedium.mixed_media',
    gouache: 'create.shared.optionNames.artMedium.gouache',
    pastel_drawing: 'create.shared.optionNames.artMedium.pastel_drawing',
    marker_art: 'create.shared.optionNames.artMedium.marker_art',
    impasto: 'create.shared.optionNames.artMedium.impasto',
    palette_knife: 'create.shared.optionNames.artMedium.palette_knife',
    '3d_sculptural_paint': 'create.shared.optionNames.artMedium.three_d_sculptural_paint',
  },
  decorStyle: {
    default: 'create.shared.optionNames.auto',
    modern: 'create.shared.optionNames.decorStyle.modern',
    minimalist: 'create.shared.optionNames.decorStyle.minimalist',
    scandinavian_japandi: 'create.shared.optionNames.decorStyle.scandinavian_japandi',
    mid_century_modern: 'create.shared.optionNames.decorStyle.mid_century_modern',
    contemporary_glam: 'create.shared.optionNames.decorStyle.contemporary_glam',
    traditional: 'create.shared.optionNames.decorStyle.traditional',
    transitional: 'create.shared.optionNames.decorStyle.transitional',
    rustic_farmhouse: 'create.shared.optionNames.decorStyle.rustic_farmhouse',
    industrial: 'create.shared.optionNames.decorStyle.industrial',
    coastal: 'create.shared.optionNames.decorStyle.coastal',
    boho: 'create.shared.optionNames.decorStyle.boho',
    vintage_retro: 'create.shared.optionNames.decorStyle.vintage_retro',
    eclectic: 'create.shared.optionNames.decorStyle.eclectic',
  },
};

export const getLocalizedOptionName = (
  categoryKey: string | undefined,
  option: OptionItem,
  t?: unknown,
): string => {
  if (!categoryKey) {
    return option.name;
  }

  const categoryMap = OPTION_NAME_TRANSLATION_KEYS[categoryKey];
  if (!categoryMap) {
    return option.name;
  }

  const optionKey = option.id === '' ? categoryMap.default : categoryMap[option.id];
  if (!optionKey) {
    return option.name;
  }

  return translateWithFallback(t, optionKey, option.name);
};

export const getLocalizedAutoOptionDescription = (t?: unknown): string => {
  return translateWithFallback(
    t,
    'create.shared.optionDescription.auto',
    'PopFrame will choose the setting that best fits your idea.',
  );
};

export const getLocalizedCustomInputStrings = (t?: unknown) => {
  return {
    title: translateWithFallback(t, 'create.shared.customInput.title', 'Custom Input'),
    subtitle: translateWithFallback(
      t,
      'create.shared.customInput.subtitle',
      'Add your own description',
    ),
    placeholder: translateWithFallback(t, 'create.shared.customInput.placeholder', 'Type details…'),
  };
};

// Quality options (required) - generated from QUALITY_TIERS hashmap
// Ordered from the lowest to highest quality tier.
// Runtime credits are fetched from the database via getTaskCreditCosts().
export const qualityOptions: QualityOption[] = [
  {
    id: 'standard',
    name: QUALITY_TIERS.standard.label,
    description: QUALITY_TIERS.standard.description,
    icon: QUALITY_TIERS.standard.iconName,
    starCount: QUALITY_TIERS.standard.starCount,
  },
  {
    id: 'pro',
    name: QUALITY_TIERS.pro.label,
    description: QUALITY_TIERS.pro.description,
    icon: QUALITY_TIERS.pro.iconName,
    starCount: QUALITY_TIERS.pro.starCount,
  },
  {
    id: 'studio',
    name: QUALITY_TIERS.studio.label,
    description: QUALITY_TIERS.studio.description,
    icon: QUALITY_TIERS.studio.iconName,
    starCount: QUALITY_TIERS.studio.starCount,
  },
];

/**
 * Merge quality options with credit costs from database
 * @param taskCreditCosts - Array of credit costs from database (via getTaskCreditCosts)
 * @returns Quality options with credits populated from database
 */
export const mergeQualityOptionsWithCredits = (
  taskCreditCosts: { name: string; creditsValue: number }[],
): QualityOption[] => {
  return qualityOptions.map(option => {
    // Match by name (e.g., "Standard" matches "Image Generation - Standard")
    const creditCost = taskCreditCosts.find(cost =>
      cost.name.toLowerCase().includes(option.name.toLowerCase()),
    );
    return {
      ...option,
      credits: creditCost?.creditsValue,
    };
  });
};

// Aspect ratio options (required)
export const aspectRatioOptions: AspectRatioOption[] = PRINT_RATIO_CATALOG.map(entry => ({
  id: entry.id,
  name: entry.shortLabel,
  ratio: entry.id,
  description: entry.description,
  preview: entry.preview,
  printSizes: [...entry.printSizes],
  defaultPrintSize: entry.defaultPrintSize,
  displayOnly: entry.displayOnly ?? false,
}));

export const getAspectRatioOption = (aspectRatio?: string | null): AspectRatioOption | null => {
  const normalizedAspectRatio = normalizeAspectRatioId(aspectRatio);
  return normalizedAspectRatio
    ? (aspectRatioOptions.find(option => option.id === normalizedAspectRatio) ?? null)
    : null;
};

export const getDefaultAspectRatioSelection = (
  aspectRatio?: string | null,
  printSize?: string | null,
): { aspectRatio: string; printSize: string | null } => {
  const resolvedAspectRatio = normalizeAspectRatioId(aspectRatio) ?? DEFAULT_PRINT_RATIO_ID;

  return {
    aspectRatio: resolvedAspectRatio,
    printSize: resolvePrintSizeForAspectRatio(resolvedAspectRatio, printSize),
  };
};

export const getAspectRatioCanvasSize = (
  aspectRatio?: string | null,
  baseSize: number = 48,
): { width: number; height: number } => {
  const option = getAspectRatioOption(aspectRatio);
  if (!option) {
    return { width: baseSize, height: baseSize };
  }

  const largestDimension = Math.max(option.preview.width, option.preview.height);
  const scale = baseSize / largestDimension;

  return {
    width: Math.max(Math.round(option.preview.width * scale), 12),
    height: Math.max(Math.round(option.preview.height * scale), 12),
  };
};

export const getAspectRatioSelectionUpdates = (
  aspectRatio: string,
  currentPrintSize?: string | null,
): { aspectRatio: string; printSize: string | null } => ({
  ...getDefaultAspectRatioSelection(aspectRatio, currentPrintSize),
});

export const getSelectedPrintSizeForAspectRatio = (
  aspectRatio?: string | null,
  printSize?: string | null,
): string | null => {
  return resolvePrintSizeForAspectRatio(aspectRatio, printSize);
};

export const formatPrintSizeLabel = (printSize?: string | null): string => {
  if (!printSize) {
    return '';
  }

  return printSize.replace(/([0-9.]+)\s*x\s*([0-9.]+)/i, '$1" x $2"');
};

export const buildPrintFormatDisplayText = (
  aspectRatioName: string,
  printSize?: string | null,
): string => {
  // During generation, print sizes are only compatibility guidance for the selected ratio.
  // We intentionally avoid displaying any concrete size as if it were a user-picked setting.
  void printSize;
  return aspectRatioName;
};

// Art styles (optional)
export const styleOptions: OptionItem[] = [
  {
    id: '',
    name: 'Let PopFrame Choose',
    description: 'PopFrame will choose the style that best fits your idea.',
    imageUrl: `${AUTO_THUMBNAILS_URL}/art_style.png`,
  },
  {
    id: 'realism',
    name: 'Realism',
    description: 'Lifelike detail, natural lighting',
    imageUrl: `${STYLES_URL}/realism.png`,
  },
  {
    id: 'photorealism',
    name: 'Photorealism',
    description: 'Hyper-detailed, photo-accurate',
    imageUrl: `${STYLES_URL}/photorealism.png`,
  },
  {
    id: 'abstract',
    name: 'Abstract',
    description: 'Shapes, forms, non-representational',
    imageUrl: `${STYLES_URL}/abstract.png`,
  },
  {
    id: 'minimalism',
    name: 'Minimalism',
    description: 'Clean lines, negative space',
    imageUrl: `${STYLES_URL}/minimalism.png`,
  },
  {
    id: 'pop_art',
    name: 'Pop Art',
    description: 'Bright colors, graphic contrast',
    imageUrl: `${STYLES_URL}/pop_art.png`,
  },
  {
    id: 'impressionism',
    name: 'Impressionism',
    description: 'Loose strokes, soft light',
    imageUrl: `${STYLES_URL}/impressionism.png`,
  },
  {
    id: 'expressionism',
    name: 'Expressionism',
    description: 'Bold color, emotional energy',
    imageUrl: `${STYLES_URL}/expressionism.png`,
  },
  {
    id: 'surrealism',
    name: 'Surrealism',
    description: 'Dreamlike, imaginative scenes',
    imageUrl: `${STYLES_URL}/surrealism.png`,
  },
  {
    id: 'cubism',
    name: 'Cubism',
    description: 'Geometric fragmented forms',
    imageUrl: `${STYLES_URL}/cubism.png`,
  },
  {
    id: 'art_nouveau',
    name: 'Art Nouveau',
    description: 'Flowing lines, organic motifs',
    imageUrl: `${STYLES_URL}/art_nouveau.png`,
  },
  {
    id: 'fauvism',
    name: 'Fauvism',
    description: 'Wild brushstrokes, vibrant hues',
    imageUrl: `${STYLES_URL}/fauvism.png`,
  },
  {
    id: 'op_art',
    name: 'Op Art',
    description: 'Optical illusions, visual motion',
    imageUrl: `${STYLES_URL}/op_art.png`,
  },
  {
    id: 'futurism',
    name: 'Futurism',
    description: 'Motion, energy, modernism',
    imageUrl: `${STYLES_URL}/futurism.png`,
  },
  {
    id: 'geometric_modern',
    name: 'Geometric Modern',
    description: 'Bold shapes, structured design',
    imageUrl: `${STYLES_URL}/geometric_modern.png`,
  },
  {
    id: 'line_art',
    name: 'Line Art',
    description: 'Simple outlines, minimal detail',
    imageUrl: `${STYLES_URL}/line_art.png`,
  },
];

// Color schemes (optional)
export const colorSchemeOptions: OptionItem[] = [
  {
    id: '',
    name: 'Let PopFrame Choose',
    description: 'PopFrame will choose the palette that best fits your idea.',
    imageUrl: `${AUTO_THUMBNAILS_URL}/color_palette.png`,
  },
  {
    id: 'cool',
    name: 'Cool',
    description: 'Blues, greens, purples - calm, refreshing',
    imageUrl: `${COLOR_SCHEMES_URL}/cool.png`,
  },
  {
    id: 'warm',
    name: 'Warm',
    description: 'Reds, oranges, yellows - inviting, fiery',
    imageUrl: `${COLOR_SCHEMES_URL}/warm.png`,
  },
  {
    id: 'pastel',
    name: 'Pastel',
    description: 'Soft, low-saturation hues',
    imageUrl: `${COLOR_SCHEMES_URL}/pastel.png`,
  },
  {
    id: 'vibrant',
    name: 'Vibrant',
    description: 'High-saturation, punchy colours',
    imageUrl: `${COLOR_SCHEMES_URL}/vibrant.png`,
  },
  {
    id: 'monochrome',
    name: 'Monochrome',
    description: 'Single hue with tints and shades',
    imageUrl: `${COLOR_SCHEMES_URL}/monochrome.png`,
  },
];

// Moods (optional)
export const moodOptions: OptionItem[] = [
  {
    id: '',
    name: 'Let PopFrame Choose',
    description: 'PopFrame will choose the mood that best fits your idea.',
    imageUrl: `${AUTO_THUMBNAILS_URL}/mood.png`,
  },
  {
    id: 'serene',
    name: 'Serene',
    description: 'Calm, peaceful, soft, gently soothing',
    imageUrl: `${MOOD_IMAGES_URL}/serene.png`,
  },
  {
    id: 'cheerful',
    name: 'Cheerful',
    description: 'Bright, uplifting, warm, happy energy',
    imageUrl: `${MOOD_IMAGES_URL}/cheerful.png`,
  },
  {
    id: 'energetic',
    name: 'Energetic',
    description: 'Bold, vivid, dynamic, high-impact feeling',
    imageUrl: `${MOOD_IMAGES_URL}/energetic.png`,
  },
  {
    id: 'mysterious',
    name: 'Mysterious',
    description: 'Shadowy, moody, atmospheric, quietly dramatic',
    imageUrl: `${MOOD_IMAGES_URL}/mysterious.png`,
  },
  {
    id: 'melancholic',
    name: 'Melancholic',
    description: 'Muted, nostalgic, emotional, softly somber',
    imageUrl: `${MOOD_IMAGES_URL}/melancholic.png`,
  },
  {
    id: 'intense',
    name: 'Intense',
    description: 'High-contrast, dramatic, powerful emotional tone',
    imageUrl: `${MOOD_IMAGES_URL}/intense.png`,
  },
];

// Room (optional)
export const roomOptions: OptionItem[] = [
  {
    id: '',
    name: 'Let PopFrame Choose',
    description: 'PopFrame will choose the room that best fits your idea.',
    imageUrl: `${AUTO_THUMBNAILS_URL}/room.png`,
  },
  {
    id: 'living_room',
    name: 'Living Room',
    description: 'Cozy gathering space',
    imageUrl: `${ROOMS_IMAGES_URL}/living_room.png`,
  },
  {
    id: 'bedroom',
    name: 'Bedroom',
    description: 'Peaceful sanctuary for rest',
    imageUrl: `${ROOMS_IMAGES_URL}/bedroom.png`,
  },
  {
    id: 'dining_room',
    name: 'Dining Room',
    description: 'Elegant space for gatherings',
    imageUrl: `${ROOMS_IMAGES_URL}/dining_room.png`,
  },
  {
    id: 'kitchen',
    name: 'Kitchen',
    description: 'Heart of the home',
    imageUrl: `${ROOMS_IMAGES_URL}/kitchen.png`,
  },
  {
    id: 'office',
    name: 'Office',
    description: 'Productive workspace with focus',
    imageUrl: `${ROOMS_IMAGES_URL}/office.png`,
  },
  {
    id: 'bathroom',
    name: 'Bathroom',
    description: 'Spa-like retreat with clean lines',
    imageUrl: `${ROOMS_IMAGES_URL}/bathroom.png`,
  },
  {
    id: 'hallway',
    name: 'Hallway',
    description: 'Welcoming transitional space',
    imageUrl: `${ROOMS_IMAGES_URL}/hallway.png`,
  },
  {
    id: 'kids_room',
    name: "Kid's Room",
    description: 'Playful and imaginative space',
    imageUrl: `${ROOMS_IMAGES_URL}/kids_room.png`,
  },
  {
    id: 'home_gym',
    name: 'Home Gym',
    description: 'Energizing fitness environment',
    imageUrl: `${ROOMS_IMAGES_URL}/home_gym.png`,
  },
  {
    id: 'outside_space',
    name: 'Outside Space',
    description: 'Outdoor living and nature',
    imageUrl: `${ROOMS_IMAGES_URL}/patio.png`,
  },
];

// Time of Day (optional)
export const timeOfDayOptions: OptionItem[] = [
  {
    id: '',
    name: 'Let PopFrame Choose',
    description: 'PopFrame will choose the time of day that best fits your idea.',
    imageUrl: `${AUTO_THUMBNAILS_URL}/time_of_day.png`,
  },
  {
    id: 'dawn',
    name: 'Dawn',
    description: 'Soft light before sunrise',
    imageUrl: `${TIME_OF_DAY_URL}/dawn.png`,
  },
  {
    id: 'sunrise',
    name: 'Sunrise',
    description: 'Golden hour, warm glow',
    imageUrl: `${TIME_OF_DAY_URL}/sunrise.png`,
  },
  {
    id: 'daylight',
    name: 'Daylight',
    description: 'Bright midday sun',
    imageUrl: `${TIME_OF_DAY_URL}/daylight.png`,
  },
  {
    id: 'afternoon',
    name: 'Afternoon',
    description: 'Warm, gentle daylight',
    imageUrl: `${TIME_OF_DAY_URL}/afternoon.png`,
  },
  {
    id: 'sunset',
    name: 'Sunset',
    description: 'Warm golden and pink',
    imageUrl: `${TIME_OF_DAY_URL}/sunset.png`,
  },
  {
    id: 'twilight',
    name: 'Twilight',
    description: 'Blue hour, soft transition',
    imageUrl: `${TIME_OF_DAY_URL}/twilight.png`,
  },
  {
    id: 'night',
    name: 'Night',
    description: 'Moonlit or ambient glow',
    imageUrl: `${TIME_OF_DAY_URL}/night.png`,
  },
];

// Lighting Style (optional)
export const lightingStyleOptions: OptionItem[] = [
  {
    id: '',
    name: 'Let PopFrame Choose',
    description: 'PopFrame will choose the lighting that best fits your idea.',
    imageUrl: `${AUTO_THUMBNAILS_URL}/lighting.png`,
  },
  {
    id: 'soft_light',
    name: 'Soft Light',
    description: 'Smooth, gentle lighting with minimal shadows',
    imageUrl: `${LIGHTING_IMAGES_URL}/soft_light.png`,
  },
  {
    id: 'bright_clear',
    name: 'Bright & Clear',
    description: 'Clean, even lighting like a sunny day',
    imageUrl: `${LIGHTING_IMAGES_URL}/bright_clear.png`,
  },
  {
    id: 'warm_glow',
    name: 'Warm Glow',
    description: 'Cozy, golden lighting with warm tones',
    imageUrl: `${LIGHTING_IMAGES_URL}/warm_glow.png`,
  },
  {
    id: 'cool_light',
    name: 'Cool Light',
    description: 'Soft bluish lighting, calm and modern',
    imageUrl: `${LIGHTING_IMAGES_URL}/cool_light.png`,
  },
  {
    id: 'dramatic_shadows',
    name: 'Dramatic Shadows',
    description: 'Bold contrast with strong highlights and shadows',
    imageUrl: `${LIGHTING_IMAGES_URL}/dramatic_shadows.png`,
  },
  {
    id: 'backlit',
    name: 'Backlit / Glow Behind',
    description: 'Light shining from behind the subject, glowing edges',
    imageUrl: `${LIGHTING_IMAGES_URL}/backlit.png`,
  },
  {
    id: 'studio_light',
    name: 'Studio Light',
    description: 'Even, polished, gallery-style lighting',
    imageUrl: `${LIGHTING_IMAGES_URL}/studio_light.png`,
  },
];

// Seasons (optional)
export const seasonOptions: OptionItem[] = [
  {
    id: '',
    name: 'Let PopFrame Choose',
    description: 'PopFrame will choose the season that best fits your idea.',
    imageUrl: `${AUTO_THUMBNAILS_URL}/season.png`,
  },
  {
    id: 'spring',
    name: 'Spring',
    description: 'Fresh greens, blossoms, renewal',
    imageUrl: `${SEASONS_URL}/spring.png`,
  },
  {
    id: 'summer',
    name: 'Summer',
    description: 'Bright, warm, sun-drenched',
    imageUrl: `${SEASONS_URL}/summer.png`,
  },
  {
    id: 'autumn',
    name: 'Autumn',
    description: 'Earthy oranges, falling leaves',
    imageUrl: `${SEASONS_URL}/autumn.png`,
  },
  {
    id: 'winter',
    name: 'Winter',
    description: 'Cool whites and blues, crisp quiet',
    imageUrl: `${SEASONS_URL}/winter.png`,
  },
];

// Art Technique (optional)
export const artMediumOptions: OptionItem[] = [
  {
    id: '',
    name: 'Let PopFrame Choose',
    description: 'PopFrame will choose the technique that best fits your idea.',
    imageUrl: `${STYLE_BOARD_THUMBNAILS_URL}/technique.png`,
  },
  {
    id: 'oil_painting',
    name: 'Oil Painting',
    description: 'Rich pigment, smooth blends',
    imageUrl: `${TECHNIQUES_URL}/oil_painting.png`,
  },
  {
    id: 'watercolor',
    name: 'Watercolor',
    description: 'Soft washes, translucent tones',
    imageUrl: `${TECHNIQUES_URL}/watercolor.png`,
  },
  {
    id: 'acrylic',
    name: 'Acrylic',
    description: 'Vibrant pigment, clean strokes',
    imageUrl: `${TECHNIQUES_URL}/acrylic.png`,
  },
  {
    id: 'ink_drawing',
    name: 'Ink Drawing',
    description: 'Crisp lines, bold contrast',
    imageUrl: `${TECHNIQUES_URL}/ink_drawing.png`,
  },
  {
    id: 'pencil_sketch',
    name: 'Pencil Sketch',
    description: 'Graphite shading, hand-drawn',
    imageUrl: `${TECHNIQUES_URL}/pencil_sketch.png`,
  },
  {
    id: 'charcoal_drawing',
    name: 'Charcoal Drawing',
    description: 'Soft smudging, deep tones',
    imageUrl: `${TECHNIQUES_URL}/charcoal_drawing.png`,
  },
  {
    id: 'digital_art',
    name: 'Digital Art',
    description: 'Synthetic textures, crisp edges',
    imageUrl: `${TECHNIQUES_URL}/digital_art.png`,
  },
  {
    id: '3d_render',
    name: '3D Render',
    description: 'Dimensional lighting, smooth forms',
    imageUrl: `${TECHNIQUES_URL}/3d_render.png`,
  },
  {
    id: 'mixed_media',
    name: 'Mixed Media',
    description: 'Layered materials, hybrid textures',
    imageUrl: `${TECHNIQUES_URL}/mixed_media.png`,
  },
  {
    id: 'gouache',
    name: 'Gouache',
    description: 'Matte finish, opaque blends',
    imageUrl: `${TECHNIQUES_URL}/gouache.png`,
  },
  {
    id: 'pastel_drawing',
    name: 'Pastel Drawing',
    description: 'Chalky texture, soft hues',
    imageUrl: `${TECHNIQUES_URL}/pastel_drawing.png`,
  },
  {
    id: 'marker_art',
    name: 'Marker Art',
    description: 'Bold strokes, modern illustration',
    imageUrl: `${TECHNIQUES_URL}/marker_art.png`,
  },
  {
    id: 'impasto',
    name: 'Impasto',
    description: 'Thick textured paint strokes',
    imageUrl: `${TECHNIQUES_URL}/impasto.png`,
  },
  {
    id: 'palette_knife',
    name: 'Palette Knife Painting',
    description: 'Sharp textured paint swipes',
    imageUrl: `${TECHNIQUES_URL}/palette_knife.png`,
  },
  {
    id: '3d_sculptural_paint',
    name: '3D Sculptural Paint',
    description: 'Raised, frosting-like forms',
    imageUrl: `${TECHNIQUES_URL}/3d_sculptural_paint.png`,
  },
];

// Centralized section header strings
export const SECTION_STRINGS = <const>{
  required: 'Required',
  recommended: 'Creative Settings (Recommended)',
  optional: 'Creative Settings (Optional)',
  artSubject: 'Art Subject',
};

// Placeholder text for the Art Subject input
export const ART_SUBJECT_PLACEHOLDER =
  "Describe what's in the art, e.g., an abstract painting, a horse trotting through a field, a vase on a table, a bird flying through a garden";

// Decor Style options
export const decorStyleOptions: OptionItem[] = [
  {
    id: '',
    name: 'Let PopFrame Choose',
    description: 'PopFrame will choose the decor style that best fits your idea.',
    imageUrl: `${AUTO_THUMBNAILS_URL}/decor_style.png`,
  },
  {
    id: 'modern',
    name: 'Modern',
    description: 'Clean lines, open spaces',
    imageUrl: `${DECOR_STYLE_URL}/modern.png`,
  },
  {
    id: 'minimalist',
    name: 'Minimalist',
    description: 'Simple, uncluttered aesthetic',
    imageUrl: `${DECOR_STYLE_URL}/minimalist.png`,
  },
  {
    id: 'scandinavian_japandi',
    name: 'Scandinavian / Japandi',
    description: 'Nordic meets Japanese simplicity',
    imageUrl: `${DECOR_STYLE_URL}/scandinavian-japandi.png`,
  },
  {
    id: 'mid_century_modern',
    name: 'Mid-Century Modern',
    description: 'Retro elegance, organic forms',
    imageUrl: `${DECOR_STYLE_URL}/mid-century-modern.png`,
  },
  {
    id: 'contemporary_glam',
    name: 'Contemporary Glam',
    description: 'Luxurious, sophisticated style',
    imageUrl: `${DECOR_STYLE_URL}/contemporary-glam.png`,
  },
  {
    id: 'traditional',
    name: 'Traditional',
    description: 'Classic, timeless elegance',
    imageUrl: `${DECOR_STYLE_URL}/traditional.png`,
  },
  {
    id: 'transitional',
    name: 'Transitional',
    description: 'Blend of traditional and modern',
    imageUrl: `${DECOR_STYLE_URL}/transitional.png`,
  },
  {
    id: 'rustic_farmhouse',
    name: 'Rustic / Farmhouse',
    description: 'Warm, natural, country charm',
    imageUrl: `${DECOR_STYLE_URL}/rustic-farmhouse.png`,
  },
  {
    id: 'industrial',
    name: 'Industrial',
    description: 'Raw materials, urban edge',
    imageUrl: `${DECOR_STYLE_URL}/industrial.png`,
  },
  {
    id: 'coastal',
    name: 'Coastal',
    description: 'Beachy, relaxed, breezy vibes',
    imageUrl: `${DECOR_STYLE_URL}/coastal.png`,
  },
  {
    id: 'boho',
    name: 'Boho',
    description: 'Eclectic, artistic, free-spirited',
    imageUrl: `${DECOR_STYLE_URL}/boho.png`,
  },
  {
    id: 'vintage_retro',
    name: 'Vintage / Retro',
    description: 'Nostalgic, throwback aesthetics',
    imageUrl: `${DECOR_STYLE_URL}/vintage-retro.png`,
  },
  {
    id: 'eclectic',
    name: 'Eclectic',
    description: 'Mix of styles, bold combinations',
    imageUrl: `${DECOR_STYLE_URL}/eclectic.png`,
  },
];

// Category group configuration with tooltips
export interface CategoryGroupConfig {
  id: 'artLook' | 'spaceStyle' | 'moodLight';
  label: string;
  tooltip: string;
  categories: {
    key:
      | 'room'
      | 'style'
      | 'decorStyle'
      | 'colorScheme'
      | 'artMedium'
      | 'mood'
      | 'timeOfDay'
      | 'lightingStyle'
      | 'season';
    label: string;
    required: boolean;
    options: { id: string; name: string; description?: string; imageUrl?: string }[];
  }[];
}

export const categoryGroups: CategoryGroupConfig[] = [
  {
    id: 'artLook',
    label: 'Art Look',
    tooltip: 'Makes it feel like real art',
    categories: [
      {
        key: 'artMedium',
        label: 'Medium / Technique',
        required: false,
        options: artMediumOptions,
      },
      { key: 'style', label: 'Art Style', required: false, options: styleOptions },
      { key: 'colorScheme', label: 'Color Palette', required: false, options: colorSchemeOptions },
    ],
  },
  {
    id: 'spaceStyle',
    label: 'Space & Style',
    tooltip: 'Where it lives + how the room feels',
    categories: [
      { key: 'room', label: 'Room', required: false, options: roomOptions },
      { key: 'decorStyle', label: 'Decor Style', required: false, options: decorStyleOptions },
    ],
  },
  {
    id: 'moodLight',
    label: 'Mood & Light',
    tooltip: 'The vibe + cinematic feel',
    categories: [
      { key: 'mood', label: 'Mood', required: false, options: moodOptions },
      { key: 'lightingStyle', label: 'Lighting', required: false, options: lightingStyleOptions },
      { key: 'timeOfDay', label: 'Time of Day', required: false, options: timeOfDayOptions },
      { key: 'season', label: 'Season', required: false, options: seasonOptions },
    ],
  },
];

// Recommended category configuration (for backwards compatibility - Art Look group)
export const recommendedCategoryConfig = [
  {
    key: <const>'artMedium',
    label: 'Medium / Technique',
    required: false,
    options: artMediumOptions,
  },
  { key: <const>'style', label: 'Art Style', required: false, options: styleOptions },
  {
    key: <const>'colorScheme',
    label: 'Color Palette',
    required: false,
    options: colorSchemeOptions,
  },
  { key: <const>'room', label: 'Room', required: false, options: roomOptions },
  { key: <const>'decorStyle', label: 'Decor Style', required: false, options: decorStyleOptions },
];

// Optional category configuration (for backwards compatibility - Mood & Light group)
export const optionalCategoryConfig = [
  { key: <const>'mood', label: 'Mood', required: false, options: moodOptions },
  {
    key: <const>'lightingStyle',
    label: 'Lighting',
    required: false,
    options: lightingStyleOptions,
  },
  { key: <const>'timeOfDay', label: 'Time of Day', required: false, options: timeOfDayOptions },
  { key: <const>'season', label: 'Season', required: false, options: seasonOptions },
];

// Combined category configuration for backwards compatibility
export const categoryConfig = [...recommendedCategoryConfig, ...optionalCategoryConfig];
